import React, { useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { IconChevronRight as ChevronRight } from '@tabler/icons-react';
import { Avatar, Button, Separator, toast } from '@heroui/react';
import { trpc } from '@/lib/trpc';
import { useAppSelector, useAppDispatch } from '@/stores/hooks';
import { logout, updateUserInfo } from '@his/shared/stores/slices/authSlice';
import { useLogout } from '@his/shared/hooks/useLogout';
import { apiClient } from '@his/shared/utils/api-client';
import { navigateWithBack } from '@/lib/navigation';
import { AvatarSelectDrawer } from './components/AvatarSelectDrawer';
import { AvatarEditor } from './components/AvatarEditor';
import { ChangePasswordDrawer } from './components/ChangePasswordDrawer';
import defaultAvatar from '@/assets/images/default-avatar.webp';

/**
 * 我的页面
 * 
 * 特性：
 * - ✅ 显示用户头像和昵称（从auth缓存读取）
 * - ✅ 点击头像弹出选择抽屉（从相册选择、拍照、取消）
 * - ✅ 头像编辑功能（圆形裁剪、旋转、上传）
 * - ✅ 编辑资料按钮（ghost按钮，带方向箭头）
 * - ✅ 修改密码入口（带方向箭头）
 * - ✅ 退出登录功能
 */
export default function MePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const { logout: handleLogout, isLoggingOut } = useLogout({
    trpc,
    dispatch,
    logout,
  });

  // 头像编辑相关状态
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editorImageSrc, setEditorImageSrc] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  // 修改密码抽屉状态
  const [changePasswordDrawerOpen, setChangePasswordDrawerOpen] = useState(false);

  // 更新个人资料（走 auth 模块，不依赖用户管理权限）
  const updateMyProfileMutation = trpc.auth.updateMyProfile.useMutation();
  // 刷新用户信息query
  const refreshUserInfoQuery = trpc.auth.refreshUserInfo.useQuery(undefined, {
    enabled: false,
  });

  // 获取头像URL，如果为空则使用默认头像
  const avatarUrl = user?.avatarUrl?.trim() ? user.avatarUrl : defaultAvatar;
  // 获取昵称，如果为空则显示"昵称未设定"
  const nickname = user?.nickname?.trim() ? user.nickname : '昵称未设定';

  // 处理文件选择
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // 验证文件类型
    if (!file.type.startsWith('image/')) {
      toast.danger('请选择图片文件');
      return;
    }

    // 创建预览URL
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result;
      if (typeof result === 'string') {
        setEditorImageSrc(result);
      }
    };
    reader.readAsDataURL(file);

    // 清空input，以便下次选择同一文件时也能触发change事件
    event.target.value = '';
  };

  // 从相册选择
  const handleSelectFromAlbum = () => {
    fileInputRef.current?.click();
  };

  // 拍照
  const handleTakePhoto = () => {
    cameraInputRef.current?.click();
  };

  // 完成头像编辑并上传
  const handleAvatarComplete = async (imageFile: File) => {
    if (!user?.id) {
      toast.danger('用户信息不存在');
      setEditorImageSrc(null);
      return;
    }

    /** HeroUI v3：`toast()` 返回 `string`，`toast.close` 仅接受 `string` */
    let loadingKey: string | undefined;
    try {
      loadingKey = toast('正在上传并更新头像…', { isLoading: true, timeout: 0 });

      const uploadResult = await apiClient.uploadFile({
        file: imageFile,
        description: '用户头像',
        urlType: 'permanent',
      });

      await updateMyProfileMutation.mutateAsync({
        avatarUrl: uploadResult.url,
      });

      const refreshedUser = await refreshUserInfoQuery.refetch();
      if (refreshedUser.data) {
        const userData = refreshedUser.data as any;
        dispatch(
          updateUserInfo({
            user: {
              id: userData.id,
              username: userData.username,
              nickname: userData.nickname ?? undefined,
              email: userData.email ?? undefined,
              mobile: userData.mobile ?? undefined,
              avatarUrl: userData.avatarUrl ?? undefined,
              status: userData.status ?? undefined,
              type: userData.type ?? undefined,
              tenantId: userData.tenantId ?? undefined,
              permissions: userData.permissions ?? undefined,
              roles: userData.roles ?? undefined,
              apps: userData.apps ?? undefined,
              spaces: userData.spaces ?? undefined,
            },
          })
        );
      }

      toast.close(loadingKey);
      toast.success('头像更新成功');
      setEditorImageSrc(null);
    } catch (error) {
      console.error('头像更新失败:', error);
      if (loadingKey !== undefined) {
        toast.close(loadingKey);
      }
      const msg =
        error instanceof Error
          ? error.message
          : typeof error === 'object' &&
              error !== null &&
              'message' in error &&
              typeof (error as { message: unknown }).message === 'string'
            ? (error as { message: string }).message
            : '头像更新失败';
      toast.danger(msg);
    }
  };

  // 取消头像编辑
  const handleAvatarCancel = () => {
    setEditorImageSrc(null);
  };

  return (
    <>
      <div className="flex flex-col min-h-full bg-background">
        {/* 上方：头像区 */}
        <div className="flex items-center justify-between px-4 py-4 bg-background">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <Avatar
              className="h-14 w-14 shrink-0 cursor-pointer"
              onClick={() => setDrawerOpen(true)}
            >
              <Avatar.Image src={avatarUrl} alt={nickname} />
              <Avatar.Fallback className="bg-default-100">
                <img src={defaultAvatar} alt="默认头像" className="w-full h-full object-cover" />
              </Avatar.Fallback>
            </Avatar>
            <span className="text-base font-medium text-foreground truncate">{nickname}</span>
          </div>
          <Button
            variant="ghost"
            className="text-default-500 hover:text-foreground shrink-0"
            onPress={() => navigateWithBack(navigate, location, '/me/edit')}
          >
            编辑资料
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        <Separator />

        {/* 下方：列表区 */}
        <div className="flex flex-col bg-background">
        {/* 修改密码 */}
        <div
          className="flex items-center justify-between px-4 py-4 cursor-pointer hover:bg-default active:bg-default/80 transition-colors"
          onClick={() => setChangePasswordDrawerOpen(true)}
        >
          <span className="text-base text-foreground">修改密码</span>
          <ChevronRight className="h-4 w-4 text-default-500 shrink-0" />
        </div>

        <Separator />

        {/* 退出登录 */}
        <div
          className="flex items-center justify-center px-4 py-4 cursor-pointer hover:bg-default active:bg-default/80 transition-colors"
          onClick={() => {
            if (!isLoggingOut) {
              handleLogout();
            }
          }}
        >
          <span className="text-base text-danger font-medium">
            {isLoggingOut ? '退出中...' : '退出登录'}
          </span>
        </div>
        </div>
      </div>

      {/* 隐藏的文件输入 */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileSelect}
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleFileSelect}
      />

      {/* 头像选择抽屉 */}
      <AvatarSelectDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        onSelectFromAlbum={handleSelectFromAlbum}
        onTakePhoto={handleTakePhoto}
      />

      {/* 头像编辑界面 */}
      {editorImageSrc && (
        <AvatarEditor
          imageSrc={editorImageSrc}
          onCancel={handleAvatarCancel}
          onComplete={handleAvatarComplete}
        />
      )}

      {/* 修改密码抽屉 */}
      <ChangePasswordDrawer
        open={changePasswordDrawerOpen}
        onOpenChange={setChangePasswordDrawerOpen}
      />
    </>
  );
}
